import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Breadcrumbs, MaskedLines } from "@/components/PageIntro";
import { StatusChip } from "@/components/landing/Apparitions";
import { EASE } from "@/components/landing/Reveal";

const Fact = ({ label, children, testid }) => (
  <div data-testid={testid} className="min-w-0">
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">{label}</p>
    <div className="mt-1.5 text-sm font-medium leading-snug text-midnight">{children}</div>
  </div>
);

export default function ApparitionHero({ entry }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} className="relative" data-testid="apparition-hero">
      <div className="apparition-hero-visual relative h-[72vh] min-h-[520px] overflow-hidden bg-midnight">
        <motion.img
          src={entry.image.url}
          alt={entry.image.alt}
          style={{ y: imgY }}
          data-testid="apparition-hero-image"
          className={`h-[115%] w-full object-cover ${entry.image.position || "object-center"}`}
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,42,67,0.96)_0%,rgba(16,42,67,0.55)_45%,rgba(16,42,67,0.25)_100%)]" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 h-32 bg-[linear-gradient(to_bottom,rgba(16,42,67,0.5),transparent)]" aria-hidden="true" />

        <div className="apparition-hero-copy absolute inset-x-0 bottom-0" data-testid="apparition-hero-copy">
          <div className="mx-auto max-w-7xl px-4 pb-32 sm:px-8 lg:px-16 lg:pb-36">
            <Breadcrumbs
              light
              items={[
                { label: "Home", to: "/" },
                { label: "Apparitions & Shrines", to: "/apparitions" },
                { label: entry.country, to: `/apparitions?country=${encodeURIComponent(entry.country)}` },
                { label: entry.short },
              ]}
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="mt-8 font-mono text-xs uppercase tracking-[0.34em] text-gold"
            >
              {entry.place} · {entry.dates}
            </motion.p>
            <h1 className="mt-4 max-w-4xl font-serif text-4xl font-semibold leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
              <MaskedLines lines={[entry.title]} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
              data-testid="apparition-quote"
              className="mt-6 max-w-2xl font-serif text-xl italic leading-relaxed text-ivory/85 sm:text-2xl"
            >
              {entry.quoteKind === "paraphrase" ? entry.quote : `“${entry.quote}”`}
              <span data-testid="apparition-quote-citation" className="mt-2 block font-sans text-xs not-italic leading-relaxed text-ivory/80">
                {entry.quoteUrl ? <a href={entry.quoteUrl} data-testid="apparition-quote-source" target="_blank" rel="noreferrer" className="underline underline-offset-4 transition-colors hover:text-gold">{entry.quoteSource}</a> : entry.quoteSource}
              </span>
            </motion.p>
          </div>
        </div>
        <p data-testid="apparition-hero-image-credit" className="absolute bottom-3 right-4 max-w-[90%] text-right font-mono text-[9px] uppercase tracking-[0.2em] text-ivory/80">
          {entry.image.credit}
        </p>
      </div>

      <div className="relative mx-auto -mt-20 max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          data-testid="quick-facts"
          className="grid gap-6 rounded-2xl border border-marian/15 bg-ivory p-6 shadow-[0_40px_80px_-40px_rgba(16,42,67,0.5)] sm:grid-cols-2 sm:p-8 lg:grid-cols-[1fr_1fr_1.2fr_0.8fr_1.6fr]"
        >
          <Fact label="Place" testid="fact-place">{entry.place}</Fact>
          <Fact label="Dates" testid="fact-dates">{entry.dates}</Fact>
          <Fact label="Visionary" testid="fact-visionary">{entry.visionary}</Fact>
          <Fact label="Feast" testid="fact-feast">{entry.feast}</Fact>
          <Fact label="Church status" testid="fact-status">
            <div className="flex flex-wrap gap-1.5">
              {entry.labels.map((l) => (
                <StatusChip key={l} labelKey={l} />
              ))}
            </div>
            <p className="mt-2 text-xs font-normal text-slateblue">{entry.authority}</p>
          </Fact>
        </motion.div>
      </div>
    </section>
  );
}
